import { useState, useEffect } from "react";
import { Button, FAQAccordion, PageHero } from "../../components";
import { MapPin, Phone, Mail, Clock, Send } from "lucide-react";
import "./KontaktPage.scss";
import { CaptchaWidget } from "../../components/CaptchaWidget/CaptchaWidget";
import { CONTACT_URL, GET_PUBLIC_DELIVERY_SCHEDULES_URL } from "../../features/UrlHelper";
import { useTenant } from "../../context/TenantContext";
import { lokalizujMapu } from "../../features/mapEmbed";
import axios from "axios";

// ─── Pomocné funkcie pre rozvrh ─────────────────────────────────────────────

const DAYS_FULL = ["Pondelok", "Utorok", "Streda", "Štvrtok", "Piatok", "Sobota", "Nedeľa"];

const parseLocalDate = (str) => {
  if (!str) return null;
  if (typeof str === "string" && str.length > 10) {
    const d = new Date(str);
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
  }
  const [y, m, d] = str.split("-").map(Number);
  return new Date(y, m - 1, d);
};

const scheduleCoversDate = (schedule, date) => {
  const from = parseLocalDate(schedule.valid_from);
  const to = schedule.valid_to ? parseLocalDate(schedule.valid_to) : null;
  if (!from) return false;
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  from.setHours(0, 0, 0, 0);
  if (d < from) return false;
  if (to) {
    to.setHours(0, 0, 0, 0);
    if (d > to) return false;
  }
  return true;
};

const getOurDayOfWeek = (date) => {
  const jsDay = date.getDay();
  return jsDay === 0 ? 7 : jsDay;
};

const getWeekMonday = (date) => {
  const d = new Date(date);
  const day = d.getDay();
  const offset = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + offset);
  d.setHours(0, 0, 0, 0);
  return d;
};

const getWeekDays = (monday) =>
  Array.from({ length: 7 }, (_, i) => {
    const d = new Date(monday);
    d.setDate(monday.getDate() + i);
    return d;
  });

const getActiveDayForDate = (date, schedules) => {
  const ourDay = getOurDayOfWeek(date);
  const validSchedules = schedules.filter((s) => scheduleCoversDate(s, date));
  const exceptions = validSchedules.filter((s) => s.valid_to !== null);
  const permanent = validSchedules.filter((s) => s.valid_to === null);
  const source = exceptions.length > 0 ? exceptions[0] : permanent.length > 0 ? permanent[0] : null;
  if (!source) return null;
  return source.days.find((d) => d.day_of_week === ourDay) ?? null;
};

function KontaktPage() {
  // Prevadzku uz nacital TenantContext -- netreba volat /api/company znova.
  const { tenant: company } = useTenant();
  const [schedules, setSchedules] = useState([]);

  const [form, setForm] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });
  const [status, setStatus] = useState("idle"); // idle | sending | success | error
  const [captchaToken, setCaptchaToken] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    axios.get(GET_PUBLIC_DELIVERY_SCHEDULES_URL).then((res) => {
      setSchedules(res.data);
    }).catch(() => {});
  }, []);

  const today = new Date();
  const todayDayIndex = getOurDayOfWeek(today) - 1; // 0=Po ... 6=Ne
  const weekDays = getWeekDays(getWeekMonday(today));

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!captchaToken) {
      alert("Prosím, potvrďte, že nie ste robot.");
      return;
    }

    setStatus("sending");
    setErrorMessage("");

    try {
      const { data } = await axios.post(CONTACT_URL, {
        ...form,
        cfTurnstileToken: captchaToken,
      });

      if (data.success) {
        setStatus("success");
        setForm({ name: "", email: "", subject: "", message: "" });
        setCaptchaToken(null);
      } else {
        setStatus("error");
        setErrorMessage(
          data.message || "Odoslanie správy zlyhalo. Skúste to znova.",
        );
      }
    } catch (error) {
      setStatus("error");
      setErrorMessage(
        error.response?.data?.message || "Chyba pripojenia. Skúste to znova.",
      );
    }
  };

  return (
    <div className="kontakt">
      {/* To isté záhlavie ako objednávka, denné menu, profil a registrácia. */}
      <PageHero
        eyebrow="Kontakt"
        title="Ozvite sa"
        zvyraznene="nám"
        lead="Zavolajte, napíšte alebo sa zastavte osobne."
      />

      <div className="kontakt__obsah">
        <div className="kontakt__grid">
          <div className="kontakt__stlpec">
            <section className="kontakt__karta">
              {company?.name && <h2 className="kontakt__karta-nadpis">{company.name}</h2>}

              <ul className="kontakt__udaje">
                <li className="kontakt__udaj">
                  <span className="kontakt__ikona"><MapPin size={19} aria-hidden="true" /></span>
                  <div>
                    <span className="kontakt__udaj-label">Adresa</span>
                    <span className="kontakt__udaj-hodnota">{company?.address ?? "—"}</span>
                  </div>
                </li>

                <li className="kontakt__udaj">
                  <span className="kontakt__ikona"><Phone size={19} aria-hidden="true" /></span>
                  <div>
                    <span className="kontakt__udaj-label">Telefón</span>
                    {company?.phone
                      ? <a className="kontakt__udaj-hodnota kontakt__odkaz" href={`tel:${company.phone}`}>{company.phone}</a>
                      : <span className="kontakt__udaj-hodnota">—</span>}
                  </div>
                </li>

                <li className="kontakt__udaj">
                  <span className="kontakt__ikona"><Mail size={19} aria-hidden="true" /></span>
                  <div>
                    <span className="kontakt__udaj-label">E-mail</span>
                    {company?.email
                      ? <a className="kontakt__udaj-hodnota kontakt__odkaz" href={`mailto:${company.email}`}>{company.email}</a>
                      : <span className="kontakt__udaj-hodnota">—</span>}
                  </div>
                </li>
              </ul>
            </section>

            <section className="kontakt__karta">
              <h2 className="kontakt__karta-nadpis">
                <Clock size={18} aria-hidden="true" /> Rozvozové hodiny
              </h2>

              <ul className="kontakt__rozvrh">
                {weekDays.map((date, i) => {
                  const dayData = getActiveDayForDate(date, schedules);
                  const isToday = i === todayDayIndex;
                  let timeLabel;
                  if (!dayData) timeLabel = "–";
                  else if (dayData.is_closed) timeLabel = "Zatvorené";
                  else if (dayData.time_from && dayData.time_to)
                    timeLabel = `${dayData.time_from.slice(0, 5)} – ${dayData.time_to.slice(0, 5)}`;
                  else timeLabel = "–";
                  return (
                    <li
                      key={i}
                      className={`kontakt__rozvrh-riadok${isToday ? " kontakt__rozvrh-riadok--dnes" : ""}`}
                    >
                      <span className="kontakt__rozvrh-den">
                        {DAYS_FULL[i]}
                        {isToday && <span className="kontakt__dnes">Dnes</span>}
                      </span>
                      <span className="kontakt__rozvrh-cas">{timeLabel}</span>
                    </li>
                  );
                })}
              </ul>
            </section>
          </div>

          <div className="kontakt__mapa">
            <iframe
              title="Mapa"
              src={lokalizujMapu(company?.google_maps_url)}
              width="100%"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </div>

        {/* Sekcia "Napíšte nám" — zatiaľ zakomentovaná, ponechať pre budúce použitie */}
        {/*
        <div className="kontakt__form-wrapper">
          <h2 className="kontakt__form-title">Napíšte nám</h2>

          {status === "success" ? (
            <div className="kontakt__success">
              <div className="kontakt__success-icon">✓</div>
              <h3>Správa odoslaná!</h3>
              <p>Ďakujeme za vašu správu. Ozveme sa vám čo najskôr.</p>
              <Button variant="ghost" onClick={() => setStatus("idle")}>
                Odoslať ďalšiu správu
              </Button>
            </div>
          ) : (
            <form className="kontakt__form" onSubmit={handleSubmit}>
              <div className="kontakt__form-row">
                <div className="kontakt__form-field">
                  <label className="kontakt__form-label">Meno</label>
                  <input
                    className="kontakt__form-input"
                    value={form.name}
                    onChange={(e) => handleChange("name", e.target.value)}
                    placeholder="Vaše meno"
                    required
                  />
                </div>
                <div className="kontakt__form-field">
                  <label className="kontakt__form-label">Email</label>
                  <input
                    type="email"
                    className="kontakt__form-input"
                    value={form.email}
                    onChange={(e) => handleChange("email", e.target.value)}
                    placeholder="vas@email.com"
                    required
                  />
                </div>
              </div>
              <div className="kontakt__form-field">
                <label className="kontakt__form-label">Predmet</label>
                <input
                  className="kontakt__form-input"
                  value={form.subject}
                  onChange={(e) => handleChange("subject", e.target.value)}
                  placeholder="O čom chcete písať?"
                  required
                />
              </div>
              <div className="kontakt__form-field">
                <label className="kontakt__form-label">Správa</label>
                <textarea
                  className="kontakt__form-textarea"
                  value={form.message}
                  onChange={(e) => handleChange("message", e.target.value)}
                  placeholder="Vaša správa..."
                  rows={5}
                  required
                />
              </div>
              <CaptchaWidget onVerify={(token) => setCaptchaToken(token)} />
              {status === "error" && (
                <p className="kontakt__form-error">{errorMessage}</p>
              )}
              <Button
                variant="primary"
                type="submit"
                disabled={status === "sending"}
              >
                <Send size={16} />
                {status === "sending" ? "Odosiela sa..." : "Odoslať správu"}
              </Button>
            </form>
          )}
        </div>
        */}
      </div>
    </div>
  );
}

export default KontaktPage;
